import { cloneElement } from 'react';
import classNames from 'classnames';
import {
  Form,
  Input,
  Stack,
  StatusLight,
  Table,
  TableColumn,
  TableNoRecords,
  TableProps,
} from '@servicetitan/design-system';
import { useEmployeeTableState } from './useEmployeeTableState';
import statusValues, {
  StatusDotColor,
} from '@modules/UserStatusModule/statusValues';
import { UserSetAvailabilityStatus } from '@api/generated/graphql';
import { sanitizePhoneNumberTo10Digits } from '@lib/utils/formatPhone/sanitizePhoneNumberTo10Digits';
import { formatToNorthAmericanNumber } from '@lib/utils/formatPhone/formatToNorthAmericanNumber';
import ErrorComponent from '@components/ErrorComponent';
import styles from './OutboundModalEmployeeTable.module.css';
import strings from './strings';
import { mainLayoutLocators } from '@locators/main-layout';
import { inboxLocators } from '@locators/inbox';
import { OutboundCallEntity } from '@lib/types/common-call-types';
import { OutboundCallDetails } from '../../types';

const PhoneNumberCell: React.FC<{ phone: string }> = ({ phone }) => {
  if (!phone) {
    return <td>{strings.cells.unassignedPhoneNumber}</td>;
  }
  return (
    <td>{formatToNorthAmericanNumber(sanitizePhoneNumberTo10Digits(phone))}</td>
  );
};

const EmployeeSelectionRadioButtonCell: React.FC<{
  checked: boolean;
  id: string;
  phone: string;
  onClickHandler: (id: string, phone: string) => void;
}> = ({ checked, id, phone, onClickHandler }) => {
  return (
    <td>
      <Form.Radio
        id={`${mainLayoutLocators.outboundAndTransferModal.employeeRowRadioBtn}`}
        checked={checked}
        label=''
        onClick={() => {
          onClickHandler(id, phone);
        }}
        disabled={!phone}
      />
    </td>
  );
};

/**
 * StatusCell Component
 *
 * This component is responsible for rendering a table cell (`<td>`) that displays
 * the status of a user with a corresponding status light indicator.
 *
 * @component
 * @param {Object} props - The properties object.
 * @param {UserSetAvailabilityStatus} props.status - The status of the user, which determines the color and text of the status light.
 *
 * @returns {JSX.Element} A table cell containing a status light with the appropriate color and status text.
 *
 * Example usage:
 * ```tsx
 * <StatusCell status={UserSetAvailabilityStatus.Online} />
 * ```
 */
const StatusCell: React.FC<{ status: UserSetAvailabilityStatus }> = ({
  status,
}) => {
  if (!status) {
    return <td />;
  }
  return (
    <td>
      <StatusLight color={StatusDotColor[status]}>
        {statusValues[status]}
      </StatusLight>
    </td>
  );
};

interface EmployeeTableProps {
  setCallDetails: (details: OutboundCallDetails) => void;
}

const employeeRowRender: TableProps['rowRender'] = (trElement, props) => {
  const shouldDisable = !props.dataItem.phone;
  if (!shouldDisable) {
    return trElement;
  }
  const trProps = {
    ...trElement.props,
    className: classNames(trElement.props.className, styles.DisabledRow),
  };
  return cloneElement(trElement, trProps);
};

/**
 * OutboundModalEmployeeTable Component
 *
 * This component renders a table of employees that can be searched and selected.
 * Once the user selects an employee, the phone number of the selected employee is set using the `setNumber` function.
 * It includes a search input, a table with pagination, and various columns such as Agent, Status, and Phone Number.
 *
 * @component
 * @param {Object} props - The properties object.
 * @param {Function} props.setNumber - A function to set the phone number of the selected employee.
 *
 * @returns {JSX.Element} A stack containing a search input and a table of employees.
 *
 * Example usage:
 * ```tsx
 * <OutboundModalEmployeeTable setNumber={setNumberFunction} />
 * ```
 *
 */
const OutboundModalEmployeeTable: React.FC<EmployeeTableProps> = ({
  setCallDetails,
}) => {
  const state = useEmployeeTableState();

  return (
    <Stack direction='column' spacing={2}>
      <Stack.Item>
        <Input
          id={inboxLocators.callBar.searchInputUser}
          icon={{ name: 'search' }}
          iconPosition='left'
          className='w-100'
          size='small'
          placeholder='Search for agent name or number'
          onChange={state.onSearchChange}
          value={state.search}
        />
      </Stack.Item>

      <Stack.Item>
        <Table
          selectable={{ enabled: true, mode: 'single' }}
          data={state.data}
          loading={state.loading}
          pageable={{ buttonCount: 3 }}
          pageSize={state.page.take}
          skip={state.page.skip}
          total={state.total}
          onPageChange={state.onPageChange}
          className={styles.EmployeeTable}
          rowHeight={40}
          id={inboxLocators.callBar.usersListTableToCall}
          rowRender={employeeRowRender}
        >
          <TableNoRecords>
            <div className='ta-center m-x-auto p-y-6' style={{ width: 420 }}>
              <ErrorComponent
                image='block'
                title='No results found'
                body='Try another search.'
              />
            </div>
          </TableNoRecords>
          <TableColumn
            width={40}
            cell={(props) =>
              EmployeeSelectionRadioButtonCell({
                checked: props.dataItem.id === state.selectedId,
                id: props.dataItem.id,
                phone: props.dataItem.phone,
                onClickHandler: (id, phone) => {
                  state.onSelectionChange(id);
                  setCallDetails({
                    number: sanitizePhoneNumberTo10Digits(phone),
                    callDisplayName: props.dataItem.name,
                    agentId: props.dataItem.id,
                    entity: OutboundCallEntity.Employees,
                  });
                },
              })
            }
          />
          <TableColumn title={strings.header.employee} field='name' />
          <TableColumn
            width={200}
            title={strings.header.status}
            field='status'
            cell={(props) => StatusCell({ status: props.dataItem.status })}
          />
          <TableColumn
            title={strings.header.phoneNumber}
            field='phone'
            cell={(props) => PhoneNumberCell({ phone: props.dataItem.phone })}
          />
        </Table>
      </Stack.Item>
    </Stack>
  );
};

export default OutboundModalEmployeeTable;
